/** Prompt Test Bed API — thin wrappers over the generated Orval clients that
 *  unwrap the response envelope and re-cast JSONField columns (typed `unknown`
 *  in the OpenAPI schema) to the stronger local types below. */
import type { MockLearnerData, TestFile } from "@api/rest/prompt-sandbox";
import {
  createLearnerTestProfile as genCreateLearnerTestProfile,
  deleteLearnerTestProfile as genDeleteLearnerTestProfile,
  getLearnerTestProfile as genGetLearnerTestProfile,
  getPromptTestRun as genGetPromptTestRun,
  listLearnerTestProfiles as genListLearnerTestProfiles,
  listPromptTestRuns as genListPromptTestRuns,
  startPromptTestRun as genStartPromptTestRun,
  updateLearnerTestProfile as genUpdateLearnerTestProfile,
} from "@api/rest/types-and-hooks";
import { REPORT_TYPE_LABELS, ReportType as ReportTypeConst } from "@pages/Templates/constants";
import type { ReportTypeValue } from "@pages/Templates/constants";

// Peels the Orval `{data, status, headers}` envelope; mirrors `unwrap` in PromptTestBed.tsx.
async function unwrap<T>(resp: { data: T | { detail?: string }; status: number }): Promise<T> {
  if (resp.status !== 200 && resp.status !== 201) {
    const detail = (resp.data as { detail?: string })?.detail ?? `HTTP ${resp.status}`;
    throw new Error(detail);
  }
  return resp.data as T;
}

// ---------------------------------------------------------------------------
// Report types — reuse `pages/Templates/constants`; never declare a new copy.
// ---------------------------------------------------------------------------

export type ReportType = ReportTypeValue;
export { ReportTypeConst as REPORT_TYPES };

/** Ordered list for pickers. Add new entries in `pages/Templates/constants.ts`. */
export const REPORT_TYPE_CHOICES: { value: ReportType; label: string }[] = [
  { value: ReportTypeConst.DIAGNOSIS_EVALUATION, label: REPORT_TYPE_LABELS[ReportTypeConst.DIAGNOSIS_EVALUATION] },
  { value: ReportTypeConst.INITIAL_ASSESSMENT, label: REPORT_TYPE_LABELS[ReportTypeConst.INITIAL_ASSESSMENT] },
  { value: ReportTypeConst.REASSESSMENT, label: REPORT_TYPE_LABELS[ReportTypeConst.REASSESSMENT] },
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Reference to a test file in the sandbox S3 pool — enough to hydrate
 *  `parsed.json` at run time and to scope inputs by file kind. */
export interface LearnerTestProfileFile {
  file_uuid: string;
  file_kind: string;
  s3_prefix: string;
  filename: string;
}

// Re-export; the canonical definition lives in prompt-sandbox.ts.
export type { MockLearnerData };

export interface LearnerTestProfile {
  id: number;
  name: string;
  /** null = global, visible to every org */
  organization_id: number | null;
  mock_learner_data: MockLearnerData;
  test_files: LearnerTestProfileFile[];
  /** Empty = universal; non-empty narrows to specific ReportType values. */
  report_types: ReportType[];
  notes: string;
  /** Non-null = scoped to that prompt's test bed only; hidden from the global list. */
  scoped_prompt_key: string | null;
  created_date: string;
  last_updated_date: string;
}

export interface LearnerTestProfileInput {
  name: string;
  organization_id?: number | null;
  mock_learner_data?: MockLearnerData;
  test_files?: LearnerTestProfileFile[];
  report_types?: ReportType[];
  notes?: string;
  /** Prompt key scopes the profile to that test bed; null on a PATCH promotes it back to universal. */
  scoped_prompt_key?: string | null;
}

/** What the server scoped out when running against a prompt — feeds the
 *  "missing inputs" / "dropped files" warnings. */
export interface PromptTestRunScopeReport {
  registry_status: "matched" | "missing";
  dropped_mock_keys: string[];
  dropped_file_kinds: string[];
  missing_input_keys: string[];
}

export interface PromptTestRun {
  id: number;
  prompt_key: string;
  learner_test_profile_id: number | null;
  workflow_id: string;
  status: "running" | "complete" | "failed";
  result: string | null;
  draft_instructions_snapshot: string | null;
  /** Full draft override at run time; null when the run was against the
   *  yaml baseline (``use_draft=false``) or the draft was empty. */
  draft_overrides_snapshot: Record<string, unknown> | null;
  mock_learner_data_snapshot: MockLearnerData;
  test_file_uuids_snapshot: LearnerTestProfileFile[];
  /** Composed prompt string sent to ``run_llm``; null until the workflow terminates. */
  llm_input_snapshot: string | null;
  /** Model the run was started with; overwritten with the resolved model
   *  on completion. Empty string = the pipeline default of the run's era. */
  model?: string;
  scope_report?: PromptTestRunScopeReport;
  created_date: string;
}

export interface StartPromptTestRunInput {
  learner_test_profile_id?: number | null;
  /** Inline overrides for test-bed tweaks that shouldn't be saved back. */
  mock_learner_data_override?: MockLearnerData;
  test_files_override?: LearnerTestProfileFile[];
  /** Per-run replacement for the prompt's declared `input_keys` — the run
   *  is scoped as if the yaml declared these inputs. */
  prompt_inputs_override?: string[];
  /** Default true — runs the draft. Set false to run the yaml baseline. */
  use_draft?: boolean;
  /** Model id from the models list; omit for the pipeline default. */
  model?: string;
  /** Ties this run into a comparison slot. Sent together with `arm`; the
   *  server forces use_draft=false so the arms differ only by model. */
  comparison_id?: number;
  arm?: "baseline" | "candidate";
}

// ---------------------------------------------------------------------------
// LearnerTestProfile CRUD
// ---------------------------------------------------------------------------

export async function listLearnerTestProfiles(opts?: {
  org_id?: number | null;
  /** Keep profiles whose `report_types` is empty (universal) or contains this value. */
  report_type?: ReportType | null;
  /** Present → universals + rows scoped to this prompt; omitted → universals
   *  only. Not in the orval bindings yet — passed as a raw query param. */
  prompt_key?: string | null;
}): Promise<LearnerTestProfile[]> {
  const resp = await genListLearnerTestProfiles({
    ...(opts?.org_id != null ? { org_id: opts.org_id } : {}),
    ...(opts?.report_type ? { report_type: opts.report_type } : {}),
    ...(opts?.prompt_key ? { prompt_key: opts.prompt_key } : {}),
  } as Parameters<typeof genListLearnerTestProfiles>[0]);
  return unwrap(resp) as unknown as LearnerTestProfile[];
}

export async function createLearnerTestProfile(input: LearnerTestProfileInput): Promise<LearnerTestProfile> {
  // Generated create expects the full LearnerTestProfile shape; cast our stronger input in.
  const resp = await genCreateLearnerTestProfile(input as unknown as Parameters<typeof genCreateLearnerTestProfile>[0]);
  return unwrap(resp) as unknown as LearnerTestProfile;
}

export async function fetchLearnerTestProfile(id: number): Promise<LearnerTestProfile> {
  const resp = await genGetLearnerTestProfile(id);
  return unwrap(resp) as unknown as LearnerTestProfile;
}

export async function updateLearnerTestProfile(
  id: number,
  input: Partial<LearnerTestProfileInput>,
): Promise<LearnerTestProfile> {
  const resp = await genUpdateLearnerTestProfile(
    id,
    input as unknown as Parameters<typeof genUpdateLearnerTestProfile>[1],
  );
  return unwrap(resp) as unknown as LearnerTestProfile;
}

export async function deleteLearnerTestProfile(id: number): Promise<void> {
  const resp = await genDeleteLearnerTestProfile(id);
  if (resp.status >= 300) {
    const detail = (resp.data as { detail?: string })?.detail ?? `HTTP ${resp.status}`;
    throw new Error(detail);
  }
}

// Clone endpoint isn't in the orval bindings yet — call via customFetch
// until the next ``bun run codegen``.
export async function cloneLearnerTestProfile(
  id: number,
  overrides: {
    name?: string;
    organization_id?: string | null;
    /** When set, the clone attaches to that prompt's test bed and is
     *  hidden from the global list. */
    scoped_prompt_key?: string | null;
  } = {},
): Promise<LearnerTestProfile> {
  const { customFetch } = await import("@api/rest/custom-fetcher");
  const resp = await customFetch<{
    data: LearnerTestProfile | { detail?: string };
    status: number;
  }>(`/api/basecamp/learner-test-profiles/${id}/clone`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(overrides),
  });
  if (resp.status !== 201) {
    const detail = (resp.data as { detail?: string })?.detail ?? `HTTP ${resp.status}`;
    throw new Error(detail);
  }
  return resp.data as LearnerTestProfile;
}

// ---------------------------------------------------------------------------
// PromptTestRun (start, list, poll)
// ---------------------------------------------------------------------------

export async function startPromptTestRun(prompt_key: string, input: StartPromptTestRunInput): Promise<PromptTestRun> {
  const resp = await genStartPromptTestRun(prompt_key, input as unknown as Parameters<typeof genStartPromptTestRun>[1]);
  return unwrap(resp) as unknown as PromptTestRun;
}

export async function listPromptTestRuns(prompt_key: string): Promise<PromptTestRun[]> {
  const resp = await genListPromptTestRuns(prompt_key);
  return unwrap(resp) as unknown as PromptTestRun[];
}

export async function getPromptTestRun(prompt_key: string, run_id: number): Promise<PromptTestRun> {
  const resp = await genGetPromptTestRun(prompt_key, run_id);
  return unwrap(resp) as unknown as PromptTestRun;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Convert a sandbox `TestFile` into the compact reference shape a profile stores. */
export function testFileToProfileRef(f: TestFile): LearnerTestProfileFile {
  // The server hydrates from `<prefix>/parsed.json`, so strip the trailing filename segment.
  const s3_prefix = f.s3_key.replace(/\/[^/]+$/, "");
  return {
    file_uuid: f.file_uuid,
    file_kind: f.file_kind,
    s3_prefix,
    filename: f.original_filename,
  };
}
